// Daftar hal yang bisa diatur hak aksesnya. Sumbernya = menu & tombol yang BENAR-BENAR ada
// di aplikasi ini, supaya tak ada baris yang bisa dicentang tapi tak berefek.
//
// Menu diambil runtime dari shell (MENU_META) supaya menu baru otomatis muncul di sini —
// yang ditulis manual hanya SUB-MENU dan AKSI, karena itu tak bisa disimpulkan sendiri.
import type { Level } from './perms';

export interface Simpul {
  key: string;
  label: string;
  anak?: Simpul[];
  /** Fitur dari daftar izin lama v0.18 yang belum ada di aplikasi ini — ditampilkan, tak bisa diatur. */
  belum?: boolean;
}

const A = (key: string, label: string, anak?: Simpul[]): Simpul => (anak ? { key, label, anak } : { key, label });
const BELUM = (key: string, label: string): Simpul => ({ key, label, belum: true });

/** Aksi umum sebuah master data. */
const crud = (m: string, apa: string): Simpul[] => [
  A(`${m}.tambah`, `➕ Tambah ${apa}`),
  A(`${m}.ubah`, `✏️ Ubah ${apa}`),
  A(`${m}.hapus`, `🗑️ Hapus ${apa}`),
];

/** Sub-menu di dalam menu ⚙️ Pengaturan & Profil (urutan sama dengan tab aslinya). */
const SUB_PENGATURAN: Simpul[] = [
  A('pengaturan.sup_sp', '🏭 Supplier & Sparepart', [
    ...crud('pengaturan.sup_sp.supplier', 'supplier'),
    ...crud('pengaturan.sup_sp.sparepart', 'sparepart'),
  ]),
  A('pengaturan.komisi', '💰 Komisi', [A('pengaturan.komisi.ubah', '✏️ Ubah skema komisi')]),
  A('pengaturan.poin', '🎁 Poin Loyalty', [A('pengaturan.poin.ubah', '✏️ Ubah aturan poin')]),
  A('pengaturan.cabang', '🏢 Cabang', [...crud('pengaturan.cabang', 'cabang'), A('pengaturan.cabang.gembok', '🔒 Gembok toko')]),
  A('pengaturan.cetak', '📄 Dokumen & Template', [
    A('pengaturan.cetak.dokumen', '✏️ Ubah susunan & teks dokumen'),
    A('pengaturan.cetak.wa', '✏️ Ubah template WhatsApp'),
    A('pengaturan.cetak.nomor', '✏️ Ubah penomoran dokumen'),
  ]),
  A('pengaturan.karyawan', '👔 Karyawan', [
    ...crud('pengaturan.karyawan', 'karyawan'),
    A('pengaturan.karyawan.suspend', '⏸️ Nonaktifkan karyawan'),
    A('pengaturan.karyawan.kelola_akun', '🔑 Kelola akun login (buat/reset password)'),
  ]),
  A('pengaturan.profil', '🏪 Profil Toko', [A('pengaturan.profil.ubah', '✏️ Ubah profil & rekening')]),
  A('pengaturan.kebijakan', '⚙️ Kebijakan', [A('pengaturan.kebijakan.ubah', '✏️ Ubah kebijakan (biaya simpan, ambang)')]),
  A('pengaturan.sla', '⏱️ SLA & Waktu', [A('pengaturan.sla.ubah', '✏️ Ubah batas waktu SLA')]),
  A('pengaturan.target', '🎯 Target / KRA', [A('pengaturan.target.ubah', '✏️ Ubah target & bobot KRA')]),
  A('pengaturan.absensi_set', '🕒 Pengaturan Absensi', [
    A('pengaturan.absensi_set.shift', '✏️ Ubah shift & jam kerja'),
    A('pengaturan.absensi_set.lokasi', '📍 Ubah lokasi & radius'),
    A('pengaturan.absensi_set.libur', '📅 Kelola hari libur'),
  ]),
  A('pengaturan.hakakses', '🔐 Hak Akses', [A('pengaturan.hakakses.ubah', '✏️ Ubah hak akses')]),
  A('pengaturan.arsip', '🗄️ Arsip Transaksi', [A('pengaturan.arsip.ubah', '📦 Arsipkan / kembalikan')]),
  A('pengaturan.storage', '💾 Penyimpanan', [
    A('pengaturan.storage.unduh', '⬇️ Unduh berkas'),
    A('pengaturan.storage.hapus', '🗑️ Hapus berkas'),
  ]),
];

/**
 * Menu yang punya tombol unduh/EXPORT. Kuncinya `<menu>.export` ditegakkan di SATU pintu
 * (`export.guard`) yang dilewati semua unduhan, jadi mencabutnya benar-benar mematikan
 * ekspor dari menu itu — bukan sekadar menyembunyikan tombol.
 * IMPOR katalog sparepart SUDAH ada (portal supplier) dan izinnya di `sup_db.impor`.
 */
const EXPORT_MENUS: [string, string][] = [
  ['lap_servis', 'Laporan Servis'], ['lap_komisi', 'Teknisi / Komisi'], ['keuangan', 'Keuangan (Laba-Rugi · Arus Kas · Posisi)'], ['bayar', 'Status Hutang & Piutang'], ['beban', 'Beban / Pengeluaran'],
  ['pelanggan', 'Pelanggan'], ['jasa', 'Jasa Servis'], ['tutup_kasir', 'Tutup Kasir'],
  ['st_Sudah_Diambil', 'Sudah Diambil'], ['rekap_absensi', 'Rekap Absensi'], ['audit', 'Audit Log'],
  ['pengaturan', 'Pengaturan (sparepart & supplier)'], ['backup', 'Backup Data'],
];
const exportAksi = (menu: string): Simpul[] => [A(`${menu}.export`, '⬇️ Export / unduh data')];

/** Aksi di dalam menu selain Pengaturan. Menu yang tak disebut di sini = hanya buka/tidak. */
export const AKSI_MENU: Record<string, Simpul[]> = {
  pengaturan: SUB_PENGATURAN,
  // Menu Penagihan dilebur jadi sub-tab di sini (Apo 21 Jul) — izinnya ikut pindah.
  perlu_diperhatikan: [
    A('perlu_diperhatikan.tagih', '💬 Kirim tagihan ke pelanggan'),
    A('perlu_diperhatikan.ingatkan', '💬 Ingatkan ambil unit'),
    A('perlu_diperhatikan.nudge', '💬 Ingatkan teknisi (internal)'),
  ],
  terima: [
    A('terima.simpan', '💾 Simpan servis masuk'),
    A('terima.dp', '💵 Terima DP di awal'),
    A('terima.prioritas', '⚡ Setel prioritas'),
    A('terima.cetak', '🖨️ Cetak tanda terima'),
  ],
  jasa: crud('jasa', 'jasa servis'),
  pelanggan: [
    ...crud('pelanggan', 'pelanggan'),
    A('pelanggan.telepon', '📞 Lihat No. HP pelanggan'),
    A('pelanggan.riwayat', '🧾 Lihat riwayat servisnya'),
  ],
  crm: [A('crm.poin', '🎁 Tukar poin'), A('crm.blast', '💬 Kirim WhatsApp')],
  beban: crud('beban', 'beban'),
  penggajian: [
    A('penggajian.slip', '📝 Buat / ubah slip gaji'),
    A('penggajian.setujui', '✅ Setujui slip'),
    A('penggajian.bayar', '💵 Bayar gaji'),
    A('penggajian.cetak', '🖨️ Cetak slip'),
  ],
  bayar: [
    A('bayar.lunas', '✅ Tandai lunas'),
    A('bayar.verifikasi', '🔍 Verifikasi pembayaran'),
    A('bayar.edit', '✏️ Ubah tagihan / batal lunas'),
    A('bayar.tagih', '💬 Kirim tagihan'),
    A('bayar.refund', '💸 Verifikasi pengembalian dana'),
  ],
  tutup_kasir: [A('tutup_kasir.tutup', '🔒 Tutup kasir harian')],
  audit: [A('audit.ajukan', '📩 Ajukan penghapusan'), A('audit.putuskan', '⚖️ Setujui / tolak permintaan')],
  backup: [A('backup.jalan', '💾 Jalankan backup'), A('backup.pulih', '♻️ Pulihkan data')],
  absensi: [A('absensi.absen', '🕒 Absen masuk/pulang'), A('absensi.koreksi', '📩 Minta koreksi')],
  rekap_absensi: [A('rekap_absensi.putuskan', '⚖️ Setujui koreksi & cuti')],
  kpi: [A('kpi.aktual', '✏️ Ubah nilai aktual KRA')],
  survei: [A('survei.simulasi', '🧪 Simulasi survei')],
  // Sparepart: aksi yang menyentuh UANG (modal tiket & hutang supplier) — ditambahkan bersama
  // fitur konfirmasi penerimaan & retur pilih-barang (0128/0130).
  sparepart: [
    A('sparepart.request', '➕ Minta sparepart ke supplier'),
    A('sparepart.terima', '✔ Konfirmasi barang diterima (mengakui modal & hutang)'),
    A('sparepart.retur', '↩️ Ajukan retur ke supplier'),
  ],
  // Portal supplier (peran Supplier). Berguna dicabut bila suatu supplier disalahgunakan —
  // mis. menagih terus-menerus atau menitipkan sembarangan.
  sup_req: [
    A('sup_req.serah', '✔ Serahkan barang'),
    A('sup_req.titip', '🔁 Titipkan ke supplier lain'),
    A('sup_req.kosong', '❌ Tandai barang kosong'),
  ],
  sup_lap: [A('sup_lap.tagih', '📣 Tagih pembayaran ke iFiCs')],
  sup_db: [
    A('sup_db.impor', '⬆️ Impor katalog (Isx/Odoo)'),
    A('sup_db.hapus', '🗑️ Hapus sparepart dari katalog'),
  ],
};

/**
 * Fitur dari daftar 108 izin v0.18 yang TIDAK punya padanan di aplikasi ini.
 * Dicantumkan supaya tak terlihat "hilang", tapi tak bisa disetel — tak ada yang menegakkannya.
 */
// Sisipkan kunci export ke tiap menu yang punya unduhan (menu tanpa aksi lain tetap dapat barisnya).
for (const [menu] of EXPORT_MENUS) AKSI_MENU[menu] = [...(AKSI_MENU[menu] ?? []), ...exportAksi(menu)];

export const BELUM_ADA: Record<string, Simpul[]> = {
  lap_servis: [
    BELUM('v18.lap_harian', 'Laporan harian terpisah'),
    BELUM('v18.lap_mingguan', 'Laporan mingguan terpisah'),
    BELUM('v18.lap_bulanan', 'Laporan bulanan terpisah'),
  ],
  bayar: [BELUM('v18.garansi_menu', 'Menu Status Garansi (digabung ke Sudah Diambil)')],
  pelanggan: [BELUM('v18.cust_import', 'Impor pelanggan dari Isx/Odoo')],
};

/** Semua kunci export ditutup — dipakai sebagai BAWAAN Manager & SPV (Owner buka per-menu bila perlu). */
const exportTertutup = (): Record<string, Level> =>
  Object.fromEntries(EXPORT_MENUS.map(([m]) => [`${m}.export`, 'none' as Level]));

/** Bawaan tiap peran untuk aksi tertentu = perilaku aplikasi hari ini (anti-regresi). */
export const BAWAAN_AKSI: Record<string, Record<string, Level>> = {
  // Pemisahan tugas anti-kecurangan (jangan dilonggarkan tanpa keputusan Apo):
  // penandaan lunas oleh CS, verifikasi oleh Finance — tak ada peran operasional yang bisa keduanya.
  CS: { ...exportTertutup(), 'bayar.verifikasi': 'none', 'bayar.edit': 'none', 'bayar.refund': 'none', 'pelanggan.hapus': 'none', 'jasa.tambah': 'none', 'jasa.ubah': 'none', 'jasa.hapus': 'none' },
  'Admin Finance': { ...exportTertutup(), 'bayar.lunas': 'none', 'bayar.edit': 'none', 'pelanggan.hapus': 'none' },
  // SPV boleh membuka ⚙️ Pengaturan HANYA untuk mengelola timnya — bagian lain tertutup.
  SPV: { ...Object.fromEntries(['sup_sp', 'komisi', 'poin', 'cabang', 'cetak', 'profil', 'kebijakan', 'sla', 'target', 'absensi_set', 'hakakses', 'arsip', 'storage'].map((t) => [`pengaturan.${t}`, 'none' as Level])),
    ...exportTertutup(),
    'pengaturan.karyawan.hapus': 'none', 'pengaturan.karyawan.kelola_akun': 'none',
    'bayar.verifikasi': 'none', 'bayar.edit': 'none', 'bayar.refund': 'none', 'bayar.lunas': 'none', 'pelanggan.hapus': 'none', 'audit.putuskan': 'none' },
  // Menu 💳 Status Hutang, Piutang memang bisa DILIHAT teknisi (piutang tiketnya sendiri),
  // tapi tak satu pun aksi uang di dalamnya. Tanpa baris ini, aksi tak dikenal mewarisi
  // level menu = 'full' — default yang salah, dan default-lah yang dipercaya saat di-wire nanti.
  Teknisi: { ...exportTertutup(), 'pelanggan.telepon': 'none', 'bayar.lunas': 'none', 'bayar.verifikasi': 'none', 'bayar.edit': 'none', 'bayar.refund': 'none', 'bayar.tagih': 'none' },
  // Kelola akun login (kredensial) = Owner-only, SoD (Apo). Manager boleh LIHAT (read), tak boleh buat/reset.
  // EXPORT bawaan TERTUTUP untuk Manager & SPV (Apo 28 Jul, anti-pencurian data): unduhan massal —
  // terutama data pelanggan — hanya Owner. Owner membuka per-menu lewat layar Hak Akses sesuai kebutuhan.
  Manager: { 'backup.pulih': 'none', 'pengaturan.karyawan.kelola_akun': 'read', ...exportTertutup() },
};

/**
 * Kunci yang TIDAK BISA dinaikkan lewat layar Hak Akses — pemisahan tugas anti-kecurangan.
 * Penandaan lunas ada di CS, verifikasi ada di Finance; tak boleh ada satu orang yang bisa
 * keduanya, sebab itulah satu-satunya yang mencegah uang masuk-keluar tanpa saksi.
 * BAWAAN_AKSI hanya mengatur nilai AWAL — daftar ini mengunci nilai TERTINGGI.
 */
export const TERKUNCI: Record<string, string[]> = {
  // Kelola akun login = kredensial → dikunci Owner-only (SoD, Apo). Tak bisa dinaikkan ke 'full'
  // lewat layar Hak Akses untuk peran mana pun selain Owner (Owner tak pernah dibatasi levelFor).
  Manager: ['pengaturan.karyawan.kelola_akun'],
  CS: ['bayar.verifikasi', 'bayar.edit', 'bayar.refund', 'penggajian', 'penggajian.slip', 'penggajian.setujui', 'penggajian.bayar', 'penggajian.cetak'],
  'Admin Finance': ['bayar.lunas', 'bayar.edit', 'penggajian.setujui'],   // Finance merakit & membayar, TAPI tak menyetujui sendiri
  SPV: ['bayar.lunas', 'bayar.verifikasi', 'bayar.edit', 'bayar.refund', 'pengaturan.karyawan.kelola_akun', 'penggajian', 'penggajian.slip', 'penggajian.setujui', 'penggajian.bayar', 'penggajian.cetak'],
  Teknisi: ['bayar.lunas', 'bayar.verifikasi', 'bayar.edit', 'bayar.refund', 'penggajian', 'penggajian.slip', 'penggajian.setujui', 'penggajian.bayar', 'penggajian.cetak'],
};

/**
 * Apakah sebuah kunci terkunci untuk peran ini — dipakai `levelFor` supaya penjagaannya berlaku
 * di semua jalur, bukan hanya membuat dropdown di layar Hak Akses jadi disabled. Setelan izin
 * tersimpan di tabel yang bisa ditulis lewat API, jadi penjagaan yang hanya ada di layar bisa
 * dilewati tanpa membuka layarnya. Owner tak pernah terkena (tak ada di daftar).
 */
export const terkunciUntuk = (role: string, key: string): boolean =>
  TERKUNCI[role]?.includes(key) ?? false;

/**
 * Kunci yang penegakannya SUDAH tersambung ke kode. Sisanya ditampilkan abu-abu
 * "belum ditegakkan" — layar keamanan yang berbohong lebih berbahaya daripada tak ada layarnya.
 * Tambahkan ke sini setiap kali sebuah aksi benar-benar di-wire.
 */
export const DITEGAKKAN = new Set<string>([
  'penggajian.slip',
  'penggajian.setujui',
  'penggajian.bayar',
  'penggajian.cetak',
  'pengaturan.sup_sp.supplier.tambah',
  'pengaturan.sup_sp.supplier.ubah',
  'pengaturan.sup_sp.supplier.hapus',
  'pengaturan.karyawan.tambah', 'pengaturan.karyawan.ubah', 'pengaturan.karyawan.suspend', 'pengaturan.karyawan.kelola_akun',
  'pelanggan.telepon',
  'perlu_diperhatikan.tagih', 'perlu_diperhatikan.ingatkan', 'perlu_diperhatikan.nudge',
  ...EXPORT_MENUS.map(([m]) => `${m}.export`),
]);
